import { envBool } from 'env';
import { z } from 'zod/v3';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableThemeToggle: z.boolean({
    description: 'Enable theme toggle in the user interface.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_THEME_TOGGLE',
  }),
  enableAccountDeletion: z.boolean({
    description: 'Enable personal account deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION',
  }),
  enableTeamDeletion: z.boolean({
    description: 'Enable team deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION',
  }),
  enableTeamAccounts: z.boolean({
    description: 'Enable team accounts.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS',
  }),
  enableTeamCreation: z.boolean({
    description: 'Enable team creation.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION',
  }),
  enablePersonalAccountBilling: z.boolean({
    description: 'Enable personal account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING',
  }),
  enableTeamAccountBilling: z.boolean({
    description: 'Enable team account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING',
  }),
  languagePriority: z
    .enum(['user', 'application'], {
      required_error: 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY',
      description: `If set to user, use the user's preferred language. If set to application, use the application's default language.`,
    })
    .default('application'),
  enableNotifications: z.boolean({
    description: 'Enable notifications functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS',
  }),
  realtimeNotifications: z.boolean({
    description: 'Enable realtime for the notifications functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_REALTIME_NOTIFICATIONS',
  }),
  enableVersionUpdater: z.boolean({
    description: 'Enable version updater',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER',
  }),
  enableUserToolsPersistence: z.boolean({
    description:
      'Enable persistence of user tool settings across new chats and page reloads',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_USER_TOOLS_PERSISTENCE',
  }),
  enableKreuzbergExtraction: z.boolean({
    description:
      'Enable Kreuzberg library for server-side text extraction (replaces legacy extraction)',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_KREUZBERG_EXTRACTION',
  }),
  enableKreuzbergOcr: z.boolean({
    description:
      'Enable OCR support in Kreuzberg for scanned documents and images',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_KREUZBERG_OCR',
  }),
  enableKreuzbergPdfOcr: z.boolean({
    description:
      'Enable OCR fallback for scanned PDFs when text extraction yields minimal content',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_KREUZBERG_PDF_OCR',
  }),
  enableKreuzbergBatchProcessing: z.boolean({
    description:
      'Enable batch processing support for multiple documents in Kreuzberg',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_KREUZBERG_BATCH_PROCESSING',
  }),
  showArtifactErrorDetails: z.boolean({
    description:
      'Show detailed error messages when artifact/document generation fails (useful for debugging)',
    required_error:
      'Provide the variable NEXT_PUBLIC_SHOW_ARTIFACT_ERROR_DETAILS',
  }),
  enablePublicAssistantDetails: z.boolean({
    description:
      'Enable viewing public assistant details (system prompt, tools). When disabled, public assistants are blackbox.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PUBLIC_ASSISTANT_DETAILS',
  }),
  enableOrganizations: z.boolean({
    description:
      'Enable the customer-facing organization workspace and the forced "create an organization" onboarding gate.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_ORGANIZATIONS',
  }),
});

const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE),
    true,
  ),
  enableAccountDeletion: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION),
    false,
  ),
  enableTeamDeletion: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION),
    false,
  ),
  enableTeamAccounts: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS),
    true,
  ),
  enableTeamCreation: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION),
    true,
  ),
  enablePersonalAccountBilling: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING),
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING),
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS),
    true,
  ),
  realtimeNotifications: getBoolean(
    envBool(process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS),
    false,
  ),
  enableVersionUpdater: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER),
    false,
  ),
  enableUserToolsPersistence: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_USER_TOOLS_PERSISTENCE),
    true,
  ),
  enableKreuzbergExtraction: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_KREUZBERG_EXTRACTION),
    false,
  ),
  enableKreuzbergOcr: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_KREUZBERG_OCR),
    false,
  ),
  enableKreuzbergPdfOcr: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_KREUZBERG_PDF_OCR),
    false,
  ),
  enableKreuzbergBatchProcessing: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_KREUZBERG_BATCH_PROCESSING),
    false,
  ),
  showArtifactErrorDetails: getBoolean(
    envBool(process.env.NEXT_PUBLIC_SHOW_ARTIFACT_ERROR_DETAILS),
    false,
  ),
  enablePublicAssistantDetails: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_PUBLIC_ASSISTANT_DETAILS),
    true,
  ),
  enableOrganizations: getBoolean(
    envBool(process.env.NEXT_PUBLIC_ENABLE_ORGANIZATIONS),
    false,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }
  if (typeof value === 'boolean') {
    return value;
  }

  return defaultValue;
}
